/**
 * AD-2.1 PR4 — ReflowOfferCard.
 *
 * The Today surface for a mid-week reflow offer. Two shapes:
 *   - kind 'card'         → actionable: summary + changed days + Accept / Dismiss.
 *   - kind 'absorbed_note' → a one-time, NON-actionable note (no Accept).
 *
 * Copy guardrails (plan): never the word "safe" or an injury guarantee; load ≠
 * mileage (the engine summary already avoids "recovered X% of your workout");
 * the absorbed note explains the deficit was left in to protect recovery. Fires
 * `onShown` once on mount so the server records the per-surface exposure
 * (idempotent).
 */

import { useEffect, useRef } from 'react';
import type { ReflowOffer } from '../../hooks/useReflowOffer';
import styles from './ReflowOfferCard.module.css';

export interface ReflowOfferCardProps {
  offer: ReflowOffer;
  onAccept: () => void;
  onDismiss: () => void;
  /** Fired once on mount to record the Today-surface exposure. */
  onShown: () => void;
}

export function ReflowOfferCard({ offer, onAccept, onDismiss, onShown }: ReflowOfferCardProps) {
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    onShown();
  }, [onShown]);

  const isNote = offer.kind === 'absorbed_note';
  const days = offer.changedDayLabels && offer.changedDayLabels.length > 0
    ? [...new Set(offer.changedDayLabels)].join(', ')
    : '';

  return (
    <section className={styles.card} role="region" aria-label="Plan adjustment" data-kind={offer.kind}>
      <p className={styles.eyebrow}>{isNote ? 'This week' : 'Adjust this week?'}</p>
      <p className={styles.summary}>{offer.summary}</p>
      {!isNote && days && (
        <p className={styles.days}>
          Updates: <span>{days}</span>
        </p>
      )}
      {!isNote && (
        <p className={styles.reassure}>This adjustment stays within your current training limits.</p>
      )}
      <div className={styles.actions}>
        {!isNote && (
          <button type="button" className={styles.accept} onClick={onAccept}>
            Update my week
          </button>
        )}
        <button type="button" className={styles.dismiss} onClick={onDismiss}>
          {isNote ? 'Got it' : 'No thanks'}
        </button>
      </div>
    </section>
  );
}
